/**
 * Pure helpers that turn the `/auth/me` user into a PostHog identify payload.
 * No side effects and no PostHog import so this can be unit-tested in node.
 */

export interface IdentityUser {
  id: string;
  role: string;
  email?: string | null;
  fullName?: string | null;
  memberships?: Array<{
    organizationId?: string;
    organization?: { id: string; name?: string | null; type?: string | null } | null;
  }> | null;
  organizationId?: string | null;
  kolProfile?: { agencyOrgId?: string | null } | null;
  signupSource?: string | null;
}

export interface IdentifyPayload {
  distinctId: string;
  person: Record<string, string>;
  superProps: Record<string, string>;
}

interface ResolvedOrg {
  id: string | null;
  type: string | null;
  name: string | null;
}

export function resolveIdentityOrg(user: IdentityUser): ResolvedOrg {
  if (user.role === 'KOL') {
    const agencyOrgId = user.kolProfile?.agencyOrgId ?? null;
    return { id: agencyOrgId || null, type: 'KOL_ROSTER', name: null };
  }
  const first = user.memberships?.[0];
  if (first?.organization?.id) {
    return {
      id: first.organization.id,
      type: first.organization.type ?? null,
      name: first.organization.name ?? null,
    };
  }
  if (first?.organizationId) {
    return { id: first.organizationId, type: null, name: null };
  }
  if (user.organizationId) {
    return { id: user.organizationId, type: null, name: null };
  }
  return { id: null, type: null, name: null };
}

/**
 * Key that changes whenever the identify payload should be re-sent
 * (e.g. onboarding creates the org after login).
 */
export function identityKey(user: IdentityUser | null | undefined): string | null {
  if (!user) return null;
  const org = resolveIdentityOrg(user);
  return `${user.id}:${org.id ?? ''}`;
}

export function buildIdentifyPayload(
  user: IdentityUser,
  opts: { sendEmail: boolean }
): IdentifyPayload {
  const org = resolveIdentityOrg(user);
  const person: Record<string, string> = { role: user.role };
  const superProps: Record<string, string> = { role: user.role };

  if (org.id) {
    person.org_id = org.id;
    superProps.org_id = org.id;
  }
  if (org.type) {
    person.org_type = org.type;
    superProps.org_type = org.type;
  }
  if (org.name) person.org_name = org.name;
  if (user.signupSource) person.signup_source = user.signupSource;

  if (opts.sendEmail) {
    if (user.email) person.email = user.email;
    if (user.fullName) person.name = user.fullName;
  }

  return { distinctId: user.id, person, superProps };
}
